// src/components/admin/ApprovalsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle, Clock, XCircle, Eye, ThumbsUp, ThumbsDown, RotateCcw, Calendar } from 'lucide-react';
import leaveService from '../../services/leave.service';
import approvalService from '../../services/approval.service';
import attendanceService from '../../services/attendance.service';
import holidayPlanService from '../../services/holidayPlan.service';
import { useEmployees } from '../../contexts/EmployeeContext';
import { toISTDateString } from '../../utils/dateHelpers';

const APPROVAL_DISPLAY_FIELDS = new Set([
    'first_name',
    'middle_name',
    'last_name',
    'display_name',
    'email',
    'work_email',
    'personal_email',
    'phone',
    'date_of_birth',
    'gender',
    'marital_status',
    'nationality',
    'country',
    'state',
    'city',
    'address',
    'postal_code',
    'joining_date',
    'exit_date',
    'employment_status',
    'employment_type',
    'designation',
    'department_id',
    'managerid',
    'manager_id',
    'reporting_manager_id',
    'secondaryaccess',
    'secondary_access',
    'work_location',
    'emergency_contact_name',
    'emergency_contact_relationship',
    'emergency_contact_phone',
    'bank_name',
    'bank_account_number',
    'bank_ifsc',
    'photo'
].map(field => field.toLowerCase()));

const VISIBLE_APPROVAL_STATUSES = new Set(['pending', 'under_review']);

const normalizeFieldKey = (field) => (field || '').toString().toLowerCase();

const LABEL_OVERRIDES = {
    first_name: 'First Name',
    middle_name: 'Middle Name',
    last_name: 'Last Name',
    display_name: 'Display Name',
    personal_email: 'Personal Email',
    email: 'Email',
    phone: 'Phone',
    date_of_birth: 'Date of Birth',
    joining_date: 'Joining Date',
    department_id: 'Department',
    department: 'Department',
    designation: 'Designation',
    employment_status: 'Employment Status',
    employment_type: 'Employment Type',
    managerId: 'Reporting Manager',
    reporting_manager_id: 'Reporting Manager',
    secondaryAccess: 'Secondary Access',
    secondary_access: 'Secondary Access'
};

const formatFieldLabel = (field) => {
    if (!field) return '';
    if (LABEL_OVERRIDES[field]) {
        return LABEL_OVERRIDES[field];
    }

    const withSpaces = field
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ');

    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        if (value.length > 3) {
            return `[${value.length} items]`;
        }
        return value.map((item) => formatChangeValue(item)).join(', ');
    }
    if (value instanceof Date) {
        return value.toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata' });
    }
    if (typeof value === 'object') {
        if (value && typeof value === 'object') {
            if (value.name || value.title || value.label) {
                return value.name || value.title || value.label;
            }
            if (value.id && value.name) {
                return `${value.name} (${value.id})`;
            }
            if (value.url || value.uri) {
                return '[File/Link]';
            }
            const keys = Object.keys(value);
            if (keys.length > 0) {
                const keyCount = keys.length;
                return `[${keyCount} field${keyCount !== 1 ? 's' : ''}]`;
            }
        }
        return '[Object]';
    }
    const stringValue = String(value);
    if (stringValue.length > 50) {
        return `${stringValue.slice(0, 50)}…`;
    }
    return stringValue;
};

const areValuesEqual = (previousValue, nextValue) => {
    const isObject = (val) => val !== null && typeof val === 'object';

    if (isObject(previousValue) || isObject(nextValue)) {
        try {
            return JSON.stringify(previousValue ?? null) === JSON.stringify(nextValue ?? null);
        } catch (error) {
            console.error('Error comparing complex values for change summary:', error);
            return false;
        }
    }

    return (previousValue ?? '') === (nextValue ?? '');
};

const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
    });
};

const normalizeEmployeeUpdateRequests = (requests = []) => {
    return requests
        .map((request) => {
            const statusKey = normalizeFieldKey(request?.status);
            if (!VISIBLE_APPROVAL_STATUSES.has(statusKey)) {
                return null;
            }

            const previousValuesRaw = request.previous_values || request.previousValues || {};
            const updatesRaw = request.updates || request.requested_changes || {};

            const filteredUpdates = {};
            const derivedChanges = [];

            const rawChanges = request?.changes;
            if (Array.isArray(rawChanges)) {
                rawChanges.forEach((change) => {
                    const key = normalizeFieldKey(change.field);
                    if (!key || !APPROVAL_DISPLAY_FIELDS.has(key)) {
                        return;
                    }
                    const fromValue = change.from ?? change.previous ?? change.old ?? null;
                    const toValue = change.to ?? change.next ?? change.new ?? null;
                    if (!areValuesEqual(fromValue, toValue)) {
                        derivedChanges.push({
                            field: change.field,
                            label: change.label || formatFieldLabel(change.field),
                            from: fromValue,
                            to: toValue
                        });
                    }
                });
            } else if (rawChanges && typeof rawChanges === 'object') {
                Object.entries(rawChanges).forEach(([field, value]) => {
                    const key = normalizeFieldKey(field);
                    if (!key || !APPROVAL_DISPLAY_FIELDS.has(key)) {
                        return;
                    }
                    const fromValue = value?.from ?? value?.previous ?? value?.old ?? null;
                    const toValue = value?.to ?? value?.next ?? value?.new ?? null;
                    if (!areValuesEqual(fromValue, toValue)) {
                        derivedChanges.push({
                            field,
                            label: value?.label || formatFieldLabel(field),
                            from: fromValue,
                            to: toValue
                        });
                    }
                });
            }

            if (derivedChanges.length === 0) {
                Object.entries(updatesRaw).forEach(([field, value]) => {
                    const key = normalizeFieldKey(field);
                    if (!key || !APPROVAL_DISPLAY_FIELDS.has(key)) {
                        return;
                    }
                    const previousValue = previousValuesRaw[field];
                    if (!areValuesEqual(previousValue, value)) {
                        filteredUpdates[field] = value;
                        derivedChanges.push({
                            field,
                            label: formatFieldLabel(field),
                            from: previousValue,
                            to: value
                        });
                    }
                });
            }

            const previousValues = Object.keys(previousValuesRaw || {}).reduce((acc, field) => {
                const key = normalizeFieldKey(field);
                if (!key || !APPROVAL_DISPLAY_FIELDS.has(key)) {
                    return acc;
                }
                acc[field] = previousValuesRaw[field];
                return acc;
            }, {});

            const employeeDisplayName =
                previousValues.display_name
                    || (previousValues.first_name || previousValues.last_name
                        ? `${previousValues.first_name || ''} ${previousValues.last_name || ''}`.trim()
                        : null)
                    || request.employee_name
                    || request.employeeName
                    || request.employee?.name
                    || 'Unknown';

            return {
                id: request.id,
                status: request.status || 'pending',
                employeeId: request.employee_id || request.employeeId || request.employee?.id,
                employeeName: employeeDisplayName,
                submittedById: request.submitted_by || request.submittedBy || request.requested_by,
                submittedByName: request.submitted_by_name
                    || request.submittedByName
                    || request.requested_by_name
                    || 'Unknown',
                submittedAt: request.submitted_at || request.created_at || request.requested_at || new Date().toISOString(),
                updates: filteredUpdates,
                previousValues,
                changes: derivedChanges,
                resolutionComments: request.resolution_comments || request.comments || '',
                reviewedAt: request.reviewed_at || request.processed_at,
                reviewedByName: request.reviewed_by_name || request.reviewer_name || '',
                metadata: request.metadata || {},
                source: request.metadata?.source || request.source || 'employee-self-service'
            };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
};

const ApprovalsManagement = () => {
    const { fetchEmployees, updateEmployee } = useEmployees();
    const [activeTab, setActiveTab] = useState('leave');
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [leaveLoading, setLeaveLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [actionReason, setActionReason] = useState('');

    const [employeeUpdateRequests, setEmployeeUpdateRequests] = useState([]);
    const [optionalHolidayRequests, setOptionalHolidayRequests] = useState([]);
    const [attendanceRequests, setAttendanceRequests] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);

    const [otherLoading, setOtherLoading] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
    const [approvalComments, setApprovalComments] = useState('');
    const [processingApproval, setProcessingApproval] = useState(false);

    // Pending counts for tab badges
    const [pendingCounts, setPendingCounts] = useState({ leave: 0, attendance: 0, other: 0 });

    // Filter states
    const [leaveFilters, setLeaveFilters] = useState({
        employee: '',
        leaveType: '',
        status: ''
    });
    const [attendanceFilters, setAttendanceFilters] = useState({
        employee: '',
        requestType: '',
        status: ''
    });
    // Bulk selection state for attendance requests
    const [selectedAttendanceIds, setSelectedAttendanceIds] = useState(new Set());
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [otherFilters, setOtherFilters] = useState({
        employee: '',
        requestedBy: '',
        status: ''
    });

    const fetchLeaveRequests = useCallback(async () => {
        try {
            setLeaveLoading(true);
            const response = await leaveService.getAllLeaveRequests();
            if (response.success) {
                setLeaveRequests(response.requests || []);
            }
        } catch (error) {
            console.error('Error fetching leave requests:', error);
        } finally {
            setLeaveLoading(false);
        }
    }, []);

    const fetchAttendanceRequests = useCallback(async () => {
        try {
            setAttendanceLoading(true);
            const response = await attendanceService.getTeamRegularizationRequests();
            const wfhResponse = await attendanceService.getAllWfhRequests(); // Admin gets ALL WFH requests

            const regularizations = response?.data?.requests || [];
            const wfhRequests = wfhResponse?.requests || wfhResponse || [];

            const allAttendanceRequests = [
                ...regularizations.map(req => ({ ...req, type: 'regularization' })),
                ...wfhRequests.map(req => ({ ...req, type: 'wfh' }))
            ];

            // Sort pending requests to the top, then by latest time
            const sortedRequests = allAttendanceRequests.sort((a, b) => {
                // First, sort by status - pending comes first
                const statusOrder = { 'pending': 0, 'under_review': 1, 'approved': 2, 'rejected': 3 };
                const statusA = statusOrder[a.status] ?? 99;
                const statusB = statusOrder[b.status] ?? 99;

                if (statusA !== statusB) {
                    return statusA - statusB;
                }

                // Within the same status, sort by latest time (most recent first)
                const timeA = new Date(a.created_at || a.requested_at || a.from_date || a.request_date || a.attendance_date || 0);
                const timeB = new Date(b.created_at || b.requested_at || b.from_date || b.request_date || b.attendance_date || 0);

                return timeB - timeA; // Most recent first
            });

            setAttendanceRequests(sortedRequests);
        } catch (error) {
            console.error('Error fetching attendance requests:', error);
            setAttendanceRequests([]);
        } finally {
            setAttendanceLoading(false);
        }
    }, []);

    const fetchEmployeeUpdateRequests = useCallback(async () => {
        try {
            setOtherLoading(true);
            const response = await approvalService.getEmployeeUpdateRequests();
            const rawRequests =
                response?.requests
                || response?.data?.requests
                || response?.data
                || [];
            setEmployeeUpdateRequests(normalizeEmployeeUpdateRequests(rawRequests));
        } catch (error) {
            console.error('Error fetching employee update requests:', error);
            setEmployeeUpdateRequests([]);
        } finally {
            setOtherLoading(false);
        }
    }, []);

    const fetchOptionalHolidayRequests = useCallback(async () => {
        try {
            const response = await holidayPlanService.getOptionalHolidayRequests();
            const requests = response?.requests || response || [];
            setOptionalHolidayRequests(requests);
        } catch (error) {
            console.error('Error fetching optional holiday requests:', error);
            setOptionalHolidayRequests([]);
        }
    }, []);

    const fetchPendingCounts = useCallback(async () => {
        try {
            const response = await approvalService.getPendingApprovalsCount();
            if (response.success) {
                setPendingCounts({
                    leave: response.leave || 0,
                    attendance: response.attendance || 0,
                    other: response.other || 0
                });
            }
        } catch (error) {
            console.error('Error fetching pending counts:', error);
        }
    }, []);

    // Fetch pending counts on mount and when data changes
    useEffect(() => {
        fetchPendingCounts();
    }, [fetchPendingCounts]);

    useEffect(() => {
        if (activeTab === 'leave') {
            fetchLeaveRequests();
        } else if (activeTab === 'attendance') {
            fetchAttendanceRequests();
        } else if (activeTab === 'other') {
            fetchEmployeeUpdateRequests();
            fetchOptionalHolidayRequests();
        }
    }, [activeTab, fetchLeaveRequests, fetchAttendanceRequests, fetchEmployeeUpdateRequests, fetchOptionalHolidayRequests]);

    // Clear selection when filters change
    useEffect(() => {
        setSelectedAttendanceIds(new Set());
    }, [attendanceFilters]);

    // Filter functions
    const filteredLeaveRequests = leaveRequests.filter(request => {
        const employeeMatch = !leaveFilters.employee ||
            (request.employee_name || '').toLowerCase().includes(leaveFilters.employee.toLowerCase()) ||
            (request.employee_code || '').toLowerCase().includes(leaveFilters.employee.toLowerCase());
        const leaveTypeMatch = !leaveFilters.leaveType ||
            (request.leave_type_name || '').toLowerCase().includes(leaveFilters.leaveType.toLowerCase());
        const statusMatch = !leaveFilters.status || request.status === leaveFilters.status;
        return employeeMatch && leaveTypeMatch && statusMatch;
    });

    const filteredAttendanceRequests = attendanceRequests.filter(request => {
        const employeeName = request.employee_name || `${request.first_name || ''} ${request.last_name || ''}`.trim();
        const employeeMatch = !attendanceFilters.employee ||
            employeeName.toLowerCase().includes(attendanceFilters.employee.toLowerCase()) ||
            (request.employee_code || '').toLowerCase().includes(attendanceFilters.employee.toLowerCase());
        const requestTypeMatch = !attendanceFilters.requestType || request.type === attendanceFilters.requestType;
        const statusMatch = !attendanceFilters.status || request.status === attendanceFilters.status;
        return employeeMatch && requestTypeMatch && statusMatch;
    });

    const filteredOtherRequests = employeeUpdateRequests.filter(request => {
        const employeeMatch = !otherFilters.employee ||
            (request.employeeName || '').toLowerCase().includes(otherFilters.employee.toLowerCase()) ||
            (request.employeeId || '').toString().toLowerCase().includes(otherFilters.employee.toLowerCase());
        const requestedByMatch = !otherFilters.requestedBy ||
            (request.submittedByName || '').toLowerCase().includes(otherFilters.requestedBy.toLowerCase());
        const statusMatch = !otherFilters.status || request.status === otherFilters.status;
        return employeeMatch && requestedByMatch && statusMatch;
    });

    const handleAction = (request, type) => {
        setSelectedRequest(request);
        setActionType(type);
        setActionReason('');
        setShowActionModal(true);
    };

    const submitAction = async () => {
        if (!selectedRequest) return;

        try {
            let response;

            if (actionType === 'under_review') {
                response = await leaveService.updateLeaveStatus(
                    selectedRequest.id,
                    'under_review'
                );
            } else if (actionType === 'approve') {
                response = await leaveService.approveLeave(selectedRequest.id);
            } else if (actionType === 'reject') {
                if (!actionReason.trim()) {
                    alert('Please provide a reason for rejection');
                    return;
                }
                response = await leaveService.rejectLeave(selectedRequest.id, actionReason);
            }

            if (response?.success) {
                alert(`Leave request ${actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'moved to review'} successfully`);
                setShowActionModal(false);
                setSelectedRequest(null);
                setActionReason('');
                fetchLeaveRequests();
                fetchPendingCounts();
            }
        } catch (error) {
            console.error('Error processing action:', error);
            alert('Failed to process request');
        }
    };

    const handleRevoke = async (request) => {
        const reason = prompt('Please provide a reason for revoking this approved leave:');
        if (!reason || reason.trim() === '') {
            alert('Reason is required to revoke an approved leave.');
            return;
        }

        try {
            const result = await leaveService.revokeApprovedLeave(request.id, reason);
            alert(result.message || 'Leave revoked successfully');
            fetchLeaveRequests();
            fetchPendingCounts();
        } catch (error) {
            console.error('Error revoking leave:', error);
            alert(error.response?.data?.error || 'Failed to revoke leave');
        }
    };

    const openApprovalModal = (request) => {
        setSelectedApprovalRequest(request);
        setApprovalComments('');
        setShowApprovalModal(true);
    };

    const closeApprovalModal = () => {
        setShowApprovalModal(false);
        setSelectedApprovalRequest(null);
        setApprovalComments('');
        setProcessingApproval(false);
    };

    const handleAttendanceAction = async (request, action, comments = '') => {
        try {
            if (request.type === 'regularization') {
                const response = await attendanceService.processRegularizationRequest(
                    request.id,
                    action,
                    comments,
                    'regular'
                );
                if (response.success) {
                    alert(`Attendance regularization ${action}d successfully`);
                }
            } else if (request.type === 'wfh') {
                const response = await attendanceService.processWfhRequest(
                    request.id,
                    action,
                    comments
                );
                if (response.success) {
                    alert(`Work from home request ${action}d successfully`);
                }
            }
            await fetchAttendanceRequests();
            await fetchPendingCounts();
        } catch (error) {
            console.error('Error processing attendance request:', error);
            alert('Failed to process attendance request');
        }
    };

    // Get pending attendance requests for bulk selection
    const pendingAttendanceRequests = filteredAttendanceRequests.filter(r => r.status === 'pending');

    // Toggle single attendance request selection
    const toggleAttendanceSelection = (requestId) => {
        setSelectedAttendanceIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(requestId)) {
                newSet.delete(requestId);
            } else {
                newSet.add(requestId);
            }
            return newSet;
        });
    };

    // Toggle all pending attendance requests selection
    const toggleAllAttendanceSelection = () => {
        if (selectedAttendanceIds.size === pendingAttendanceRequests.length && pendingAttendanceRequests.length > 0) {
            // All are selected, deselect all
            setSelectedAttendanceIds(new Set());
        } else {
            // Select all pending requests
            setSelectedAttendanceIds(new Set(pendingAttendanceRequests.map(r => r.id)));
        }
    };

    // Bulk approve attendance requests
    const handleBulkAttendanceAction = async (action) => {
        if (selectedAttendanceIds.size === 0) return;

        const confirmMessage = action === 'approve'
            ? `Are you sure you want to approve ${selectedAttendanceIds.size} attendance request(s)?`
            : `Are you sure you want to reject ${selectedAttendanceIds.size} attendance request(s)?`;

        if (!window.confirm(confirmMessage)) return;

        setBulkProcessing(true);
        let successCount = 0;
        let failCount = 0;

        const selectedRequests = attendanceRequests.filter(r => selectedAttendanceIds.has(r.id));

        for (const request of selectedRequests) {
            try {
                if (request.type === 'regularization') {
                    const response = await attendanceService.processRegularizationRequest(
                        request.id,
                        action,
                        action === 'reject' ? 'Bulk rejected' : '',
                        'regular'
                    );
                    if (response.success) successCount++;
                    else failCount++;
                } else if (request.type === 'wfh') {
                    const response = await attendanceService.processWfhRequest(
                        request.id,
                        action,
                        action === 'reject' ? 'Bulk rejected' : ''
                    );
                    if (response.success) successCount++;
                    else failCount++;
                }
            } catch (error) {
                console.error(`Error processing request ${request.id}:`, error);
                failCount++;
            }
        }

        setBulkProcessing(false);
        setSelectedAttendanceIds(new Set());

        if (failCount === 0) {
            alert(`Successfully ${action}d ${successCount} request(s)`);
        } else {
            alert(`${action === 'approve' ? 'Approved' : 'Rejected'} ${successCount} request(s). Failed: ${failCount}`);
        }

        await fetchAttendanceRequests();
        await fetchPendingCounts();
    };

    const submitEmployeeUpdateAction = async (action) => {
        if (!selectedApprovalRequest || !['approve', 'reject'].includes(action)) {
            return;
        }

        if (action === 'reject' && !approvalComments.trim()) {
            alert('Please provide a reason for rejection');
            return;
        }

        try {
            setProcessingApproval(true);
            const trimmedComments = approvalComments.trim();
            const employeeId = selectedApprovalRequest.employeeId;

            if (action === 'approve') {
                const updatesPayload = selectedApprovalRequest.updates || {};
                const previousValues = selectedApprovalRequest.previousValues || {};

                if (employeeId && updatesPayload && Object.keys(updatesPayload).length > 0) {
                    await updateEmployee(employeeId, updatesPayload);
                }

                try {
                    await approvalService.approveEmployeeUpdateRequest(selectedApprovalRequest.id, {
                        comments: trimmedComments
                    });
                } catch (error) {
                    if (employeeId && previousValues && Object.keys(previousValues).length > 0) {
                        try {
                            await updateEmployee(employeeId, previousValues);
                        } catch (rollbackError) {
                            console.error('Failed to rollback employee profile after approval error:', rollbackError);
                        }
                    }
                    throw error;
                }

                alert('Employee profile update approved');
                if (typeof fetchEmployees === 'function') {
                    await fetchEmployees(true);
                }
            } else {
                await approvalService.rejectEmployeeUpdateRequest(selectedApprovalRequest.id, {
                    comments: trimmedComments
                });
                alert('Employee profile update rejected');
            }

            closeApprovalModal();
            await fetchEmployeeUpdateRequests();
            await fetchPendingCounts();
        } catch (error) {
            console.error('Error processing employee update request:', error);
            const message = error.response?.data?.message
                || error.response?.data?.error
                || error.message
                || 'Failed to process employee update request';
            alert(message);
        } finally {
            setProcessingApproval(false);
        }
    };

    const handleApproveOptionalHoliday = async (requestId) => {
        try {
            await holidayPlanService.approveOptionalHoliday(requestId);
            alert('Optional holiday request approved successfully');
            await fetchOptionalHolidayRequests();
            await fetchPendingCounts();
        } catch (error) {
            console.error('Error approving optional holiday request:', error);
            alert(error.response?.data?.error || 'Failed to approve optional holiday request');
        }
    };

    const handleRejectOptionalHoliday = async (requestId) => {
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason || reason.trim() === '') {
            alert('Reason is required to reject the request.');
            return;
        }
        try {
            await holidayPlanService.rejectOptionalHoliday(requestId, reason.trim());
            alert('Optional holiday request rejected');
            await fetchOptionalHolidayRequests();
            await fetchPendingCounts();
        } catch (error) {
            console.error('Error rejecting optional holiday request:', error);
            alert(error.response?.data?.error || 'Failed to reject optional holiday request');
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: {
                icon: Clock,
                class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                label: 'Pending'
            },
            under_review: {
                icon: Eye,
                class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
                label: 'Under Review'
            },
            approved: {
                icon: CheckCircle,
                class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                label: 'Approved'
            },
            rejected: {
                icon: XCircle,
                class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                label: 'Rejected'
            }
        };

        const config = statusConfig[status] || statusConfig.pending;
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.class}`}>
                <Icon className="w-4 h-4" />
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'Asia/Kolkata'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';

        // Handle different time formats
        // If it's already in HH:MM format, return as is
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours);
            const min = minutes;
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${displayHour}:${min} ${period}`;
        }

        // If it's a full date-time string, parse it
        try {
            const date = new Date(timeString);
            if (isNaN(date.getTime())) return timeString;

            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'Asia/Kolkata'
            });
        } catch (error) {
            return timeString;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow">
                <div className="px-6 py-4">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Approvals</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Review and manage leave requests and other approvals
                    </p>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('leave')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'leave'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            Leave Approvals
                            {pendingCounts.leave > 0 && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                    {pendingCounts.leave > 99 ? '99+' : pendingCounts.leave}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('attendance')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'attendance'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            Attendance Approvals
                            {pendingCounts.attendance > 0 && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                    {pendingCounts.attendance > 99 ? '99+' : pendingCounts.attendance}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('other')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'other'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            Other Approvals
                            {pendingCounts.other > 0 && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                    {pendingCounts.other > 99 ? '99+' : pendingCounts.other}
                                </span>
                            )}
                        </button>
                    </nav>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {activeTab === 'leave' ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                        {leaveLoading ? (
                            <div className="p-8 text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading leave requests...</p>
                            </div>
                        ) : leaveRequests.length === 0 ? (
                            <div className="p-8 text-center">
                                <CheckCircle className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                                <p className="text-gray-600 dark:text-gray-400">No leave requests pending approval</p>
                            </div>
                        ) : (
                            <div className="overflow-hidden">
                                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Employee
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Leave Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Dates
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Days
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Actions
                                                </th>
                                            </tr>
                                            <tr className="bg-gray-50 dark:bg-gray-900">
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <input
                                                        type="text"
                                                        placeholder="Filter..."
                                                        value={leaveFilters.employee}
                                                        onChange={(e) => setLeaveFilters({...leaveFilters, employee: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <input
                                                        type="text"
                                                        placeholder="Filter..."
                                                        value={leaveFilters.leaveType}
                                                        onChange={(e) => setLeaveFilters({...leaveFilters, leaveType: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <select
                                                        value={leaveFilters.status}
                                                        onChange={(e) => setLeaveFilters({...leaveFilters, status: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="under_review">Under Review</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="rejected">Rejected</option>
                                                    </select>
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {filteredLeaveRequests.map((request) => (
                                            <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {request.employee_name || 'Unknown'}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {request.employee_code}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {request.leave_type_name || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {formatDate(request.from_date)} - {formatDate(request.to_date)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {request.days_requested} day(s)
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(request.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {request.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleAction(request, 'under_review')}
                                                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                                            >
                                                                <Eye className="w-5 h-5 inline mr-1" />
                                                                Review
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction(request, 'reject')}
                                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                            >
                                                                <XCircle className="w-5 h-5 inline mr-1" />
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {request.status === 'under_review' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleAction(request, 'approve')}
                                                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                                                            >
                                                                <ThumbsUp className="w-5 h-5 inline mr-1" />
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction(request, 'reject')}
                                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                            >
                                                                <ThumbsDown className="w-5 h-5 inline mr-1" />
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {request.status === 'approved' && (
                                                        <button
                                                            onClick={() => handleRevoke(request)}
                                                            className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 flex items-center gap-1"
                                                            title="Revoke approved leave"
                                                        >
                                                            <RotateCcw className="w-4 h-4" />
                                                            <span>Revoke</span>
                                                        </button>
                                                    )}
                                                    {request.status === 'rejected' && (
                                                        <span className="text-gray-400 dark:text-gray-600">No actions</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </div>
                        )}
                    </div>
                ) : activeTab === 'attendance' ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                        {attendanceLoading ? (
                            <div className="p-8 text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading attendance requests...</p>
                            </div>
                        ) : attendanceRequests.length === 0 ? (
                            <div className="p-12 text-center">
                                <CheckCircle className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    Attendance Approvals
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400">
                                    No attendance requests pending approval.
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                                    Attendance regularization and work from home requests will appear here for your review.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-hidden">
                                {/* Bulk Action Bar */}
                                {selectedAttendanceIds.size > 0 && (
                                    <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                                {selectedAttendanceIds.size} request{selectedAttendanceIds.size > 1 ? 's' : ''} selected
                                            </span>
                                            <button
                                                onClick={() => setSelectedAttendanceIds(new Set())}
                                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                            >
                                                Clear selection
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleBulkAttendanceAction('approve')}
                                                disabled={bulkProcessing}
                                                className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                            >
                                                {bulkProcessing ? (
                                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                                ) : (
                                                    <ThumbsUp className="w-4 h-4" />
                                                )}
                                                Approve Selected
                                            </button>
                                            <button
                                                onClick={() => handleBulkAttendanceAction('reject')}
                                                disabled={bulkProcessing}
                                                className="px-3 py-1.5 text-sm font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                            >
                                                {bulkProcessing ? (
                                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                                ) : (
                                                    <ThumbsDown className="w-4 h-4" />
                                                )}
                                                Reject Selected
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-4 py-3 text-center bg-gray-50 dark:bg-gray-900 w-12">
                                                    <input
                                                        type="checkbox"
                                                        checked={pendingAttendanceRequests.length > 0 && selectedAttendanceIds.size === pendingAttendanceRequests.length}
                                                        onChange={toggleAllAttendanceSelection}
                                                        disabled={pendingAttendanceRequests.length === 0}
                                                        className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 disabled:opacity-50"
                                                        title={pendingAttendanceRequests.length > 0 ? "Select all pending requests" : "No pending requests to select"}
                                                    />
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Employee
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Request Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Details
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Actions
                                                </th>
                                            </tr>
                                            <tr className="bg-gray-50 dark:bg-gray-900">
                                                <th className="px-4 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <input
                                                        type="text"
                                                        placeholder="Filter..."
                                                        value={attendanceFilters.employee}
                                                        onChange={(e) => setAttendanceFilters({...attendanceFilters, employee: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <select
                                                        value={attendanceFilters.requestType}
                                                        onChange={(e) => setAttendanceFilters({...attendanceFilters, requestType: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="wfh">Work From Home</option>
                                                        <option value="regularization">Attendance Regularization</option>
                                                    </select>
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <select
                                                        value={attendanceFilters.status}
                                                        onChange={(e) => setAttendanceFilters({...attendanceFilters, status: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="under_review">Under Review</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="rejected">Rejected</option>
                                                    </select>
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {filteredAttendanceRequests.map((request) => (
                                            <tr key={request.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedAttendanceIds.has(request.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                                                <td className="px-4 py-4 text-center w-12">
                                                    {request.status === 'pending' ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAttendanceIds.has(request.id)}
                                                            onChange={() => toggleAttendanceSelection(request.id)}
                                                            className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                                        />
                                                    ) : (
                                                        <span className="text-gray-300 dark:text-gray-600">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {request.employee_name || `${request.first_name || ''} ${request.last_name || ''}`.trim() || 'Unknown'}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {request.employee_code || request.employeeId}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                                        request.type === 'wfh'
                                                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                                            : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                                    }`}>
                                                        {request.type === 'wfh' ? 'Work From Home' : 'Attendance Regularization'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {request.type === 'wfh'
                                                            ? (request.from_date && request.to_date && request.from_date !== request.to_date
                                                                ? `${formatDate(request.from_date)} - ${formatDate(request.to_date)}`
                                                                : formatDate(request.from_date || request.request_date))
                                                            : formatDate(request.attendance_date)
                                                        }
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {request.type === 'wfh'
                                                            ? request.reason
                                                            : (
                                                                <div>
                                                                    <div>Check In: {formatTime(request.requested_punch_in)}</div>
                                                                    <div>Check Out: {formatTime(request.requested_punch_out)}</div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                        {request.reason}
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(request.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {request.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleAttendanceAction(request, 'approve')}
                                                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                                                            >
                                                                <ThumbsUp className="w-4 h-4 inline mr-1" />
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={() => handleAttendanceAction(request, 'reject', 'Request rejected')}
                                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-3"
                                                            >
                                                                <ThumbsDown className="w-4 h-4 inline mr-1" />
                                                                Reject
                                                            </button>
                                                            {/* Review link - opens employee logs in new tab */}
                                                            {request.type === 'regularization' && (
                                                                <button
                                                                    onClick={() => {
                                                                        const date = request.attendance_date ? toISTDateString(request.attendance_date) : '';
                                                                        window.open(`/admin/employees/${request.employee_id}?tab=logs&date=${date}`, '_blank');
                                                                    }}
                                                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-xs"
                                                                    title="View employee activity logs for this date"
                                                                >
                                                                    <Eye className="w-3 h-3 inline mr-0.5" />
                                                                    Review
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    {request.status !== 'pending' && (
                                                        <span className="text-gray-400 dark:text-gray-600">No actions</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Optional Holiday Requests Section */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-blue-600" />
                                    Optional Holiday Requests
                                    {optionalHolidayRequests.filter(r => r.status === 'pending').length > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                            {optionalHolidayRequests.filter(r => r.status === 'pending').length}
                                        </span>
                                    )}
                                </h3>
                            </div>
                            {optionalHolidayRequests.length === 0 ? (
                                <div className="p-8 text-center">
                                    <Calendar className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                                    <p className="text-gray-600 dark:text-gray-400">
                                        No optional holiday requests
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Employee
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Holiday
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Requested
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {optionalHolidayRequests.map(request => (
                                                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {request.employee_name || 'Unknown'}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {request.employee_code}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {request.holiday_name}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900 dark:text-white">
                                                            {formatDate(request.holiday_date)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {formatDateTime(request.requested_at)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getStatusBadge(request.status)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        {request.status === 'pending' ? (
                                                            <>
                                                                <button
                                                                    onClick={() => handleApproveOptionalHoliday(request.id)}
                                                                    className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                                                                >
                                                                    <ThumbsUp className="w-4 h-4 inline mr-1" />
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRejectOptionalHoliday(request.id)}
                                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                                >
                                                                    <ThumbsDown className="w-4 h-4 inline mr-1" />
                                                                    Reject
                                                                </button>
                                                            </>
                                                        ) : request.status === 'rejected' && request.rejection_reason ? (
                                                            <span className="text-sm text-gray-500 dark:text-gray-400" title={request.rejection_reason}>
                                                                Reason: {request.rejection_reason.length > 30
                                                                    ? request.rejection_reason.substring(0, 30) + '...'
                                                                    : request.rejection_reason}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 dark:text-gray-600">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Employee Update Requests Section */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                    <Eye className="w-5 h-5 text-purple-600" />
                                    Employee Update Requests
                                    {employeeUpdateRequests.filter(r => ['pending', 'under_review'].includes(r.status)).length > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                            {employeeUpdateRequests.filter(r => ['pending', 'under_review'].includes(r.status)).length}
                                        </span>
                                    )}
                                </h3>
                            </div>
                        {otherLoading ? (
                            <div className="p-8 text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <p className="mt-2 text-gray-600 dark:text-gray-400">
                                    Loading employee update requests...
                                </p>
                            </div>
                        ) : employeeUpdateRequests.length === 0 ? (
                            <div className="p-8 text-center">
                                <CheckCircle className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                                <p className="text-gray-600 dark:text-gray-400">
                                    No employee update requests
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-hidden">
                                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Employee
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Requested By
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Submitted
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Changes Requested
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">
                                                    Actions
                                                </th>
                                            </tr>
                                            <tr className="bg-gray-50 dark:bg-gray-900">
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <input
                                                        type="text"
                                                        placeholder="Filter..."
                                                        value={otherFilters.employee}
                                                        onChange={(e) => setOtherFilters({...otherFilters, employee: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <input
                                                        type="text"
                                                        placeholder="Filter..."
                                                        value={otherFilters.requestedBy}
                                                        onChange={(e) => setOtherFilters({...otherFilters, requestedBy: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900">
                                                    <select
                                                        value={otherFilters.status}
                                                        onChange={(e) => setOtherFilters({...otherFilters, status: e.target.value})}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="under_review">Under Review</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="rejected">Rejected</option>
                                                    </select>
                                                </th>
                                                <th className="px-6 py-2 bg-gray-50 dark:bg-gray-900"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {filteredOtherRequests.map(request => (
                                            <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {request.employeeName}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {request.employeeId}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {request.submittedByName}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {formatDateTime(request.submittedAt)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="space-y-2">
                                                        {request.changes.slice(0, 3).map(change => (
                                                            <div key={change.field} className="text-sm text-gray-900 dark:text-white">
                                                                <span className="font-medium">
                                                                    {change.label}:
                                                                </span>{' '}
                                                                {formatChangeValue(change.from)} → {formatChangeValue(change.to)}
                                                            </div>
                                                        ))}
                                                        {request.changes.length === 0 && (
                                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                No change details provided.
                                                            </div>
                                                        )}
                                                        {request.changes.length > 3 && (
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                + {request.changes.length - 3} more change(s)
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(request.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <button
                                                        onClick={() => openApprovalModal(request)}
                                                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                    >
                                                        <Eye className="w-5 h-5 inline mr-1" />
                                                        Review
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </div>
                        )}
                        </div>
                    </div>
                )}
            </div>

            {/* Action Modal */}
            {showActionModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={() => setShowActionModal(false)}></div>

                        <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                {actionType === 'approve' && 'Approve Leave Request'}
                                {actionType === 'reject' && 'Reject Leave Request'}
                                {actionType === 'under_review' && 'Move to Under Review'}
                            </h3>

                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    Employee: <span className="font-medium text-gray-900 dark:text-white">
                                        {selectedRequest?.employee_name}
                                    </span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    Dates: <span className="font-medium text-gray-900 dark:text-white">
                                        {selectedRequest && `${formatDate(selectedRequest.from_date)} - ${formatDate(selectedRequest.to_date)}`}
                                    </span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Days: <span className="font-medium text-gray-900 dark:text-white">
                                        {selectedRequest?.days_requested}
                                    </span>
                                </p>
                            </div>

                            {actionType === 'reject' && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Reason for Rejection *
                                    </label>
                                    <textarea
                                        value={actionReason}
                                        onChange={(e) => setActionReason(e.target.value)}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Please provide a reason for rejection..."
                                        required
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowActionModal(false)}
                                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitAction}
                                    className={`px-4 py-2 text-white rounded-lg ${
                                        actionType === 'approve'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : actionType === 'reject'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {actionType === 'approve' && 'Approve'}
                                    {actionType === 'reject' && 'Reject'}
                                    {actionType === 'under_review' && 'Move to Review'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Employee Update Approval Modal */}
            {showApprovalModal && selectedApprovalRequest && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div
                            className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"
                            onClick={closeApprovalModal}
                        ></div>

                        <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                Employee Update Request
                            </h3>

                            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">Employee</p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {selectedApprovalRequest.employeeName}
                                    </p>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {selectedApprovalRequest.employeeId}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">Requested By</p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {selectedApprovalRequest.submittedByName}
                                    </p>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {formatDateTime(selectedApprovalRequest.submittedAt)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">Status</p>
                                    <p className="font-medium text-gray-900 dark:text-white capitalize">
                                        {selectedApprovalRequest.status.replace(/_/g, ' ')}
                                    </p>
                                </div>
                                {selectedApprovalRequest.reviewedByName && (
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400">Reviewed By</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedApprovalRequest.reviewedByName}
                                        </p>
                                        {selectedApprovalRequest.reviewedAt && (
                                            <p className="text-gray-500 dark:text-gray-400">
                                                {formatDateTime(selectedApprovalRequest.reviewedAt)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                    Requested Changes
                                </h4>
                                <div className="bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                                    {selectedApprovalRequest.changes.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            No change details provided.
                                        </p>
                                    ) : (
                                        selectedApprovalRequest.changes.map(change => (
                                            <div key={change.field}>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {change.label}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {formatChangeValue(change.from)} → {formatChangeValue(change.to)}
                                                </p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {selectedApprovalRequest.resolutionComments && selectedApprovalRequest.status === 'rejected' && (
                                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                                        Rejection Reason
                                    </p>
                                    <p className="text-sm text-red-600 dark:text-red-200 mt-1">
                                        {selectedApprovalRequest.resolutionComments}
                                    </p>
                                </div>
                            )}

                            {['pending', 'under_review'].includes(selectedApprovalRequest.status) && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Comments (required for rejection)
                                    </label>
                                    <textarea
                                        value={approvalComments}
                                        onChange={(e) => setApprovalComments(e.target.value)}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Add context for approvers or explain rejection."
                                    />
                                </div>
                            )}

                            <div className="mb-6 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 p-4">
                                <p className="text-sm text-blue-700 dark:text-blue-200">
                                    Approving will immediately apply these changes to the employee profile. Rejection keeps the employee record unchanged and notifies the requester via this queue.
                                </p>
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={closeApprovalModal}
                                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                    disabled={processingApproval}
                                >
                                    Close
                                </button>
                                {['pending', 'under_review'].includes(selectedApprovalRequest.status) && (
                                    <>
                                        <button
                                            onClick={() => submitEmployeeUpdateAction('reject')}
                                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50"
                                            disabled={processingApproval}
                                        >
                                            {processingApproval ? 'Processing...' : 'Reject'}
                                        </button>
                                        <button
                                            onClick={() => submitEmployeeUpdateAction('approve')}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                                            disabled={processingApproval}
                                        >
                                            {processingApproval ? 'Processing...' : 'Approve'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalsManagement;
